import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Icon from './Icon';

// Guides that always carry the crisis-support helpline box.
export const HELPLINE_SLUGS = [
  'crisis-support',
  'depression-management',
  'anxiety-and-worry-management',
  'trauma-coping-capacity',
  'grief-processing-and-acceptance',
];

const callout = 'p-space-lg rounded-lg bg-primary-container border-l-4 border-surface-tint';

const HELPLINE_LINES = [
  'If you are struggling or feel unsafe, please reach out. You don’t have to face it alone.',
  'India: Tele-MANAS 14416 (free, 24×7, in many Indian languages)',
  'USA: call or text 988 · Canada: call or text 9-8-8',
  'UK: Samaritans 116 123, or NHS 111 (mental health option) · Australia: Lifeline 13 11 14',
  'Elsewhere: contact your local emergency number or nearest hospital.',
  'If someone is in immediate danger, call your local emergency number now (112 in India).',
];

export const HelplineBox = () => (
  <div className={callout} data-testid="helpline-box">
    <div className="flex items-start gap-space-md">
      <Icon name="favorite" className="text-surface-tint mt-1 text-[24px] shrink-0" />
      <div>
        <p className="font-title-md text-title-md text-outline uppercase tracking-wider mb-space-xs">Need to talk to someone now?</p>
        {HELPLINE_LINES.map((line, i) => (
          <p key={line} className={`font-body-md text-body-md text-on-surface-variant leading-relaxed${i === HELPLINE_LINES.length - 1 ? '' : ' mb-space-xs'}`}>{line}</p>
        ))}
      </div>
    </div>
  </div>
);

const Block = ({ heading, children, testId }) => (
  <div className="bg-surface p-space-xl rounded-lg shadow-sm" data-testid={testId}>
    <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-md">{heading}</h2>
    {children}
  </div>
);

const Para = ({ text }) => (
  <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-sm whitespace-pre-line">{text}</p>
);

const ActionList = ({ title, items }) => (
  <div className="mb-space-md">
    <span className="font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold block mb-space-xs">{title}</span>
    <ul className="list-disc pl-5 flex flex-col gap-space-xs">
      {items.map((a) => (
        <li key={a} className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{a}</li>
      ))}
    </ul>
  </div>
);

const PURIFY = {
  ALLOWED_TAGS: ['p', 'h3', 'ol', 'ul', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'strong', 'em', 'a', 'br'],
  ALLOWED_ATTR: ['start', 'class', 'href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
};

// Sanitise, then make any table scroll sideways on small screens.
const safeHtml = (raw) => DOMPurify.sanitize(String(raw || ''), PURIFY)
  .replace(/<table/g, '<div class="guide-table-scroll"><table')
  .replace(/<\/table>/g, '</table></div>');

const HtmlBody = ({ html }) => (
  <div className="guide-html" dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />
);

export const assetUrl = (file) => (file ? `/${String(file).replace(/^\/+/, '')}` : null);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const prettyMonth = (value) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(value || ''));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : value;
};

const GuideImage = ({ image, lazy, testId }) => {
  if (!image || !image.file) return null;
  return (
    <img
      src={assetUrl(image.file)}
      alt={image.alt || ''}
      width={image.width || undefined}
      height={image.height || undefined}
      loading={lazy ? 'lazy' : 'eager'}
      decoding={lazy ? 'async' : 'sync'}
      className="w-full h-auto rounded-lg shadow-sm"
      data-testid={testId}
    />
  );
};

const sectionTestId = (heading) => `guide-section-${heading.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

// Everything here is optional: a block only renders when its field is filled.
export const GuideContent = ({ topic, siteUrl }) => {
  const t = topic || {};
  const sections = (t.sections || []).filter((s) => s && s.heading && (s.html || s.rich_text));
  const graphics = (t.graphics || []).filter((g) => g && g.file);
  const faq = (t.faq || []).filter((f) => f && f.question && f.answer);
  const sources = (t.sources || []).filter((s) => s && s.title);
  const actions = t.actions || {};
  const hasActions = ['beginner', 'intermediate', 'advanced'].some((k) => (actions[k] || []).length);
  const showHelpline = t.show_helpline === true || HELPLINE_SLUGS.includes(t.slug);
  const hasByline = t.byline || t.sources_line || t.last_updated || t.next_review;
  const doctorSection = t.when_to_see_doctor && typeof t.when_to_see_doctor === 'object'
    ? t.when_to_see_doctor
    : (t.when_to_see_doctor ? { heading: 'When to see a doctor', text: t.when_to_see_doctor } : null);
  const nextStep = typeof t.next_step === 'string' ? { label: t.next_step } : (t.next_step || null);

  const ld = [];
  if (t.quick_answer || sections.length) {
    ld.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: t.display_title || t.title,
      description: t.meta_description || t.quick_answer,
      author: { '@type': 'Organization', name: 'OVERALL WELLBEING' },
      publisher: { '@type': 'Organization', name: 'OVERALL WELLBEING' },
      ...(t.date_published ? { datePublished: t.date_published } : {}),
      ...(t.date_modified || t.date_published ? { dateModified: t.date_modified || t.date_published } : {}),
      ...(siteUrl && t.share_image ? { image: `${siteUrl}${assetUrl(t.share_image)}` } : {}),
      ...(siteUrl ? { mainEntityOfPage: `${siteUrl}/topics/${t.slug}` } : {}),
    });
  }
  if (siteUrl) {
    ld.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Library', item: `${siteUrl}/resources` },
        { '@type': 'ListItem', position: 3, name: t.display_title || t.title, item: `${siteUrl}/topics/${t.slug}` },
      ],
    });
  }
  if (faq.length) {
    ld.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  const anything = showHelpline || t.hero_image || t.quick_answer || sections.length || hasActions
    || doctorSection || faq.length || sources.length || nextStep || t.try_this_today || hasByline;
  if (!anything) return null;

  return (
    <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low" data-testid="guide-content">
      <div className="max-w-4xl mx-auto flex flex-col gap-space-lg">
        {ld.map((obj, i) => (
          <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }} />
        ))}

        <GuideImage image={t.hero_image} lazy={false} testId="guide-hero-image" />

        {showHelpline ? <HelplineBox /> : null}

        {hasByline ? (
          <div className="flex flex-wrap items-center gap-space-md" data-testid="guide-byline">
            {t.byline ? <span className="font-label-md text-label-md uppercase tracking-wider text-outline">{t.byline}</span> : null}
            {t.sources_line ? <span className="font-body-sm text-body-sm text-on-surface-variant">{t.sources_line}</span> : null}
            {t.last_updated ? <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">Last updated: {prettyMonth(t.last_updated)}</span> : null}
            {t.next_review ? <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">Next review: {prettyMonth(t.next_review)}</span> : null}
          </div>
        ) : null}

        {t.quick_answer ? (
          <div className={callout} data-testid="guide-quick-answer">
            <p className="font-title-md text-title-md text-outline uppercase tracking-wider mb-space-xs">The short answer</p>
            <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{t.quick_answer}</p>
          </div>
        ) : null}

        {t.global_note ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed" data-testid="guide-global-note">{t.global_note}</p>
        ) : null}

        {t.doctor_note ? (
          <div className={callout} data-testid="guide-doctor-note">
            <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{t.doctor_note}</p>
          </div>
        ) : null}

        {sections.map((s) => (
          <React.Fragment key={s.heading}>
            {s.style === 'callout' ? (
              <div className={callout} data-testid={sectionTestId(s.heading)}>
                <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-md">{s.heading}</h2>
                {s.html ? <HtmlBody html={s.html} /> : <Para text={s.rich_text} />}
              </div>
            ) : (
              <Block heading={s.heading} testId={sectionTestId(s.heading)}>
                {s.html ? <HtmlBody html={s.html} /> : <Para text={s.rich_text} />}
              </Block>
            )}
            {graphics
              .filter((g) => g.after_section === s.heading)
              .map((g) => (
                <GuideImage key={g.file} image={g} lazy testId={`guide-graphic-${g.file.split('/').pop().replace(/\.[a-z]+$/, '')}`} />
              ))}
          </React.Fragment>
        ))}

        {hasActions ? (
          <Block heading="Things you can try" testId="guide-actions">
            {(actions.beginner || []).length ? <ActionList title="Beginner" items={actions.beginner} /> : null}
            {(actions.intermediate || []).length ? <ActionList title="Intermediate" items={actions.intermediate} /> : null}
            {(actions.advanced || []).length ? <ActionList title="Advanced" items={actions.advanced} /> : null}
          </Block>
        ) : null}

        {t.try_this_today ? (
          <div className={callout} data-testid="guide-try-today">
            <p className="font-title-md text-title-md text-outline uppercase tracking-wider mb-space-xs">Try this today</p>
            <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{t.try_this_today}</p>
          </div>
        ) : null}

        {doctorSection ? (
          <Block heading={doctorSection.heading || 'When to see a doctor'} testId="guide-when-to-see-doctor">
            {doctorSection.html ? <HtmlBody html={doctorSection.html} /> : <Para text={doctorSection.text} />}
          </Block>
        ) : null}

        {faq.length ? (
          <Block heading="Common questions" testId="guide-faq">
            {faq.map((f) => (
              <div key={f.question} className="mb-space-md">
                <p className="font-title-md text-title-md text-outline font-bold mb-space-xs">{f.question}</p>
                <Para text={f.answer} />
              </div>
            ))}
          </Block>
        ) : null}

        {sources.length ? (
          <Block heading="Sources" testId="guide-sources">
            <ul className="flex flex-col gap-space-sm">
              {sources.map((s) => (
                <li key={s.title} className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-surface-tint font-semibold hover:text-primary transition-colors">{s.title}</a>
                  ) : s.title}
                  {s.publisher ? ` — ${s.publisher}` : ''}{s.year ? ` (${s.year})` : ''}
                  {s.evidence_strength ? (
                    <span className="ml-space-xs px-2 py-0.5 rounded-full bg-surface-container-low font-label-sm text-label-sm uppercase text-outline">{s.evidence_strength}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Block>
        ) : null}

        {nextStep && nextStep.label ? (
          nextStep.to ? (
            <Link
              to={nextStep.to}
              data-testid="guide-next-step"
              className="p-space-lg rounded-lg bg-surface hover:bg-primary-container transition-colors shadow-sm flex items-center justify-between group"
            >
              <div>
                <span className="font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold block mb-space-xs">Your next step</span>
                <span className="font-title-md text-title-md text-outline font-bold group-hover:text-surface-tint transition-colors">{nextStep.label}</span>
              </div>
              <Icon name="arrow_forward" className="text-outline group-hover:text-surface-tint transition-colors" />
            </Link>
          ) : (
            <div className="p-space-lg rounded-lg bg-surface shadow-sm" data-testid="guide-next-step">
              <span className="font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold block mb-space-xs">Your next step</span>
              <span className="font-title-md text-title-md text-outline font-bold">{nextStep.label}</span>
            </div>
          )
        ) : null}
      </div>
    </section>
  );
};

export default GuideContent;
